import {
  getCustomAttributeInputType,
  filterCustomAttributes,
  getStandardAttributeInputType,
  isCustomAttribute,
} from 'dashboard/helper/automationHelper';

export function useEditableAutomation() {
  /**
   * This function sets the conditions for automation.
   * It help to format the conditions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing conditions to manifest.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @param {Function} getConditionDropdownValues - fn to fetch dropdown options for a condition key.
   * @returns {Array} An array of manifested conditions.
   */
  const manifestConditions = (
    automation,
    allCustomAttributes,
    automationTypes,
    getConditionDropdownValues
  ) => {
    const customAttributes = filterCustomAttributes(allCustomAttributes);

    return automation.conditions.map(condition => {
      const customAttr = isCustomAttribute(
        customAttributes,
        condition.attribute_key
      );

      let inputType = 'plain_text';

      if (customAttr) {
        inputType = getCustomAttributeInputType(customAttr.type);
      } else {
        inputType = getStandardAttributeInputType(
          automationTypes,
          automation.event_name,
          condition.attribute_key
        );
      }

      if (inputType === 'time_range') {
        const rawValues = Array.isArray(condition.values)
          ? condition.values
          : [];

        const from = rawValues[0] || '';
        const to = rawValues[1] || '';

        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: [from, to],
        };
      }

      if (inputType === 'plain_text' || inputType === 'date') {
        return {
          ...condition,
          values: Array.isArray(condition.values)
            ? condition.values[0]
            : condition.values,
        };
      }

      if (inputType === 'comma_separated_plain_text') {
        const arr = Array.isArray(condition.values)
          ? condition.values
          : [condition.values].filter(Boolean);

        return {
          ...condition,
          values: arr.join(','),
        };
      }

      if (inputType === 'checkbox_select') {
        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: Array.isArray(condition.values) ? [...condition.values] : [],
        };
      }

      const options = getConditionDropdownValues
        ? getConditionDropdownValues(condition.attribute_key) || []
        : [];

      return {
        ...condition,
        query_operator: condition.query_operator || 'and',
        values: options.filter(item =>
          Array.isArray(condition.values)
            ? condition.values.includes(item.id)
            : condition.values === item.id
        ),
      };
    });
  };

  /**
   * Generates action params for message-type actions (send_message, add_private_note).
   * @param {Array} params - The action parameters.
   * @returns {Array} Array containing the message string.
   */
  const generateMessageAction = params => {
    const raw = Array.isArray(params) && params.length > 0 ? params[0] : params;
    const value = typeof raw === 'string' ? raw : '';
    return [value];
  };

  /**
   * Generates action params for team_message actions.
   * @param {Array} params - The action parameters.
   * @param {Function} getActionDropdownValues - Function to fetch dropdown options.
   * @param {string} actionName - The action name.
   * @returns {Object} Object containing team_ids array and message string.
   */
  const generateTeamMessageAction = (
    params,
    getActionDropdownValues,
    actionName
  ) => {
    const [payload] = params || [];
    const selectedIds = (payload && payload.team_ids) || [];

    const options = getActionDropdownValues
      ? getActionDropdownValues(actionName) || []
      : [];

    return {
      team_ids: options.filter(opt => selectedIds.includes(opt.id)),
      message: (payload && payload.message) || '',
    };
  };

  /**
   * Generates action params for select-type actions (multi_select, search_select).
   * @param {Array} params - The action parameters.
   * @param {Function} getActionDropdownValues - Function to fetch dropdown options.
   * @param {string} actionName - The action name.
   * @returns {Array} Array of selected option objects.
   */
  const generateSelectAction = (
    params,
    getActionDropdownValues,
    actionName
  ) => {
    const options = getActionDropdownValues
      ? getActionDropdownValues(actionName) || []
      : [];

    return options.filter(item =>
      Array.isArray(params) ? params.includes(item.id) : params === item.id
    );
  };

  /**
   * Returns the default action params as a shallow copy.
   * @param {Array} params - The action parameters.
   * @returns {Array} Shallow copy of params or empty array.
   */
  const generateDefaultAction = params => {
    return Array.isArray(params) ? [...params] : [];
  };

  // Strategy map for action type handlers
  const ACTION_HANDLERS = {
    multi_select: generateSelectAction,
    search_select: generateSelectAction,
    team_message: generateTeamMessageAction,
  };

  // Action names that use message-type handler
  const MESSAGE_ACTION_NAMES = ['send_message', 'add_private_note'];

  /**
   * Generates an array of actions for the automation.
   * Uses a strategy pattern to dispatch to appropriate handlers based on action type.
   * @param {Object} action - The action object.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @param {Function} getActionDropdownValues - fn to fetch dropdown options for an action.
   * @returns {Array|Object} Generated actions array or object based on input type.
   */
  const generateActionsArray = (
    action,
    automationActionTypes,
    getActionDropdownValues
  ) => {
    const params = action.action_params || [];
    const actionName = action.action_name;

    // Handle message-type actions first (by action name)
    if (MESSAGE_ACTION_NAMES.includes(actionName)) {
      return generateMessageAction(params);
    }

    const actionType = automationActionTypes.find(
      item => item.key === actionName
    );

    if (!actionType) {
      return generateDefaultAction(params);
    }

    const inputType = actionType.inputType;
    const handler = ACTION_HANDLERS[inputType];

    if (handler) {
      return handler(params, getActionDropdownValues, actionName);
    }

    return generateDefaultAction(params);
  };

  /**
   * This function sets the actions for automation.
   * It help to format the actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing actions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @param {Function} getActionDropdownValues - fn to fetch dropdown options for actions.
   * @returns {Array} An array of manifested actions.
   */
  const manifestActions = (
    automation,
    automationActionTypes,
    getActionDropdownValues
  ) => {
    return automation.actions.map(action => ({
      ...action,
      action_params:
        action.action_params && action.action_params.length
          ? generateActionsArray(
              action,
              automationActionTypes,
              getActionDropdownValues
            )
          : [],
    }));
  };

  /**
   * Formats the automation object for use when we edit the automation.
   * It help to format the conditions and actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object to format.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @param {Function} getConditionDropdownValues - fn to fetch condition options.
   * @param {Function} getActionDropdownValues - fn to fetch action options.
   * @returns {Object} A new object with formatted automation data.
   */
  const formatAutomation = (
    automation,
    allCustomAttributes,
    automationTypes,
    automationActionTypes,
    getConditionDropdownValues,
    getActionDropdownValues
  ) => {
    return {
      ...automation,
      conditions: manifestConditions(
        automation,
        allCustomAttributes,
        automationTypes,
        getConditionDropdownValues
      ),
      actions: manifestActions(
        automation,
        automationActionTypes,
        getActionDropdownValues
      ),
    };
  };

  return { formatAutomation };
}
